import { useState, type FormEvent } from 'react';
import { Button, Card, CardHeader, Identifier, SelectField, TextField, Toggle } from '@sparkery/ui';
import { partnerSlug } from '@sparkery/contracts';
import { ApiError } from '../../lib/api.ts';
import { useCreatePartner, useWorkosOrganizations } from '../../lib/partner-admin.ts';

/**
 * Onboarding a partner.
 *
 * Two fields carry warnings rather than help text, because both are decisions that
 * cannot be undone from this screen:
 *
 * - **The link tag** is derived from the name and shown live, because it is what every
 *   referral link and every Analytics row will carry. Renaming the partner later does
 *   not move it.
 * - **The terms** are set here and nowhere else. A fixed-fee partner never starts
 *   accruing, and a commission partner keeps their ledger — changing either would
 *   rewrite what somebody is owed, so it means a new partner record instead.
 *
 * The WorkOS organisation defaults to being created from the new partner record. If
 * someone has already created the org in WorkOS, the dropdown can map it here instead.
 *
 * Opening it belongs to the page, not to this component: the trigger is the page
 * header's Add partner button, so the form cannot also own a card of its own to hold a
 * second one.
 */
const CREATE_WORKOS_ORG = '__create__';

export function NewPartnerForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [accrues, setAccrues] = useState(true);
  const [rate, setRate] = useState('');
  const [workosOrgId, setWorkosOrgId] = useState(CREATE_WORKOS_ORG);
  const create = useCreatePartner();
  const workosOrganizations = useWorkosOrganizations();

  const slug = partnerSlug(name);
  const ready = name.trim() && adminEmail.trim() && (!accrues || rate.trim()) && slug;
  const workosUnavailable =
    workosOrganizations.error instanceof ApiError && workosOrganizations.error.status === 503;
  const workosHint = workosOrganizations.isLoading
    ? 'Loading existing organisations.'
    : workosUnavailable
      ? 'WorkOS management is not configured. Create New will create the partner without a sign-in mapping.'
      : workosOrganizations.isError
        ? 'Existing organisations could not be loaded. Create New will still create one.'
        : undefined;
  const workosOptions = [
    { value: CREATE_WORKOS_ORG, label: 'Create New...' },
    ...(workosOrganizations.data?.organizations ?? []).map((organization) => ({
      value: organization.id,
      label: organization.linkedPartnerName
        ? `${organization.name} - linked to ${organization.linkedPartnerName}`
        : organization.name,
      disabled: organization.linkedPartnerId !== null,
    })),
  ];

  function reset() {
    setName('');
    setAdminEmail('');
    setAccrues(true);
    setRate('');
    setWorkosOrgId(CREATE_WORKOS_ORG);
    create.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;

    create.mutate(
      {
        name: name.trim(),
        adminEmail: adminEmail.trim(),
        commissionAccrues: accrues,
        ...(accrues ? { commissionRate: rate.trim() } : {}),
        ...(workosOrgId === CREATE_WORKOS_ORG ? {} : { workosOrgId }),
      },
      { onSuccess: () => reset() },
    );
  }

  return (
    <Card variant="panel" className="newPartnerFormCard">
      <CardHeader title="New partner" meta="Terms are set once, here" />

      <form className="partnerDetailForm" onSubmit={submit}>
        <div className="partnerDetailEditGrid">
          <TextField
            id="new-partner-name"
            label="Company name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Bodyline"
            autoComplete="off"
            hint={
              slug ? (
                <>
                  {/* The derived tag, shown live. Not commentary: it is permanent, and this is
                    the only moment anybody gets to see it before it is fixed. */}
                  Link tag <Identifier>{slug}</Identifier>
                </>
              ) : undefined
            }
          />

          <TextField
            id="new-partner-email"
            label="Admin email"
            type="email"
            value={adminEmail}
            onChange={(event) => setAdminEmail(event.target.value)}
            placeholder="john@bodyline.com"
            autoComplete="off"
          />

          <div className="partnerDetailToggleField partnerDetailCommissionToggle">
            <span className="partnerDetailFieldLabel">Commission</span>
            <Toggle
              label="Commission accrues"
              checked={accrues}
              onChange={(next) => {
                setAccrues(next);
                if (!next) setRate('');
              }}
              labelHidden
            />
          </div>

          <TextField
            id="new-partner-rate"
            label="Rate"
            value={accrues ? rate : 'Fixed fee'}
            onChange={(event) => setRate(event.target.value)}
            placeholder="12"
            inputMode="decimal"
            autoComplete="off"
            disabled={!accrues}
            readOnly={!accrues}
          />
        </div>

        <SelectField
          id="new-partner-org"
          label="WorkOS organisation"
          value={workosOrgId}
          onChange={(event) => setWorkosOrgId(event.target.value)}
          options={workosOptions}
          hint={workosHint}
        />

        <span className="partnersFormNote">
          {accrues
            ? 'Commission accrual is set at invite and cannot be changed later.'
            : 'This partner will be paid a fixed amount agreed separately. Accrual cannot be enabled later.'}
        </span>

        <div className="partnersFormActions partnerDetailSaveActions">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="confirm" disabled={!ready || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create partner'}
          </Button>
        </div>
      </form>

      {create.error ? (
        <p className="partnersFormError" role="alert">
          {create.error instanceof ApiError
            ? create.error.message
            : 'Could not create that partner.'}
        </p>
      ) : null}

      {create.data ? (
        <p className="partnersFormOk" role="status">
          <strong>{create.data.partner.name}</strong> created.
        </p>
      ) : null}
    </Card>
  );
}
